/**
 * Album order. Oldest set first, so a reprint keeps the earliest prefix.
 * Add a prefix here when a set joins the legal pool.
 */
export const ALBUM_PREFIXES = ['LOB', 'SDY', 'SDK', 'MRD'] as const

export type AlbumSetPrefix = (typeof ALBUM_PREFIXES)[number]

export type AlbumSetRef = {
  set_code?: string
  set_rarity?: string
}

/** `LOB-027` → `LOB`. Empty when the code has no prefix. */
export function albumPrefixOf(code: string): string {
  const dash = (code || '').indexOf('-')
  return dash > 0 ? code.slice(0, dash).toUpperCase() : ''
}

/**
 * North American collector line (`MRD-001`). Lettered codes
 * (`MRD-EN001`, `MRL-E103`) are a different printing and are skipped.
 */
export function albumNumericPrinting(
  sets: readonly AlbumSetRef[],
  prefix: string,
): AlbumSetRef | undefined {
  const re = new RegExp(`^${prefix}-\\d`, 'i')
  return sets.find((set) => re.test(set.set_code || ''))
}

function anyPrefix(
  sets: readonly AlbumSetRef[],
  prefix: string,
): AlbumSetRef | undefined {
  const re = new RegExp(`^${prefix}-`, 'i')
  return sets.find((set) => re.test(set.set_code || ''))
}

/**
 * One home printing for the All chip and collection missions.
 * LOB before SDY before SDK before MRD, then whatever is left.
 * A set chip uses `albumNumericPrinting` instead, so a reprint keeps
 * this home and also appears under the later set's own number.
 */
export function pickAlbumSet(
  sets: readonly AlbumSetRef[],
): AlbumSetRef | undefined {
  for (const prefix of ALBUM_PREFIXES) {
    const hit = albumNumericPrinting(sets, prefix) ?? anyPrefix(sets, prefix)
    if (hit) return hit
  }
  return sets[0]
}
